import { useSyncExternalStore } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AB_MANDATORY_PRIVACY_CHECKBOX_CLASS } from '@/components/PolicyAcceptanceCheckbox'

export const AB_PRIVACY_DISCLAIMER_CONTROL_CLASS =
  'ab-privacy-disclaimer-control'

export type PrivacyAbVariant = 'checkbox' | 'disclaimer'

const getPrivacyAbVariantFromDom = (): PrivacyAbVariant => {
  if (document.body.classList.contains(AB_MANDATORY_PRIVACY_CHECKBOX_CLASS)) {
    return 'checkbox'
  }

  return 'disclaimer'
}

const getPrivacyAbVariantFromSearchParams = (
  searchParams: URLSearchParams
): PrivacyAbVariant | null => {
  const queryVariant = searchParams.get('privacyAb')

  if (queryVariant === 'checkbox') {
    return 'checkbox'
  }

  if (queryVariant === 'disclaimer' || queryVariant === 'control') {
    return 'disclaimer'
  }

  return null
}

const getPrivacyAbVariantSnapshot = (
  searchParams: URLSearchParams
): PrivacyAbVariant => {
  return (
    getPrivacyAbVariantFromSearchParams(searchParams) ??
    getPrivacyAbVariantFromDom()
  )
}

const subscribeToPrivacyAbVariant = (onStoreChange: () => void) => {
  const observer = new MutationObserver(onStoreChange)

  observer.observe(document.body, {
    attributeFilter: ['class'],
    attributes: true,
  })

  return () => observer.disconnect()
}

/**
 * A/B test variant for the privacy block on the insert-data step.
 *
 * - Control (default): legacy disclaimer (`.ab-privacy-disclaimer-control`)
 * - Treatment: mandatory checkbox when `<body>` has `.ab-mandatory-privacy-checkbox`
 *
 * The body class can be toggled live (e.g. DevTools) without reloading the page.
 *
 * Local preview of treatment: `?privacyAb=checkbox`
 * Local preview of control: default or `?privacyAb=disclaimer`
 */
export const usePrivacyAbVariant = (): PrivacyAbVariant => {
  const [searchParams] = useSearchParams()

  return useSyncExternalStore(
    subscribeToPrivacyAbVariant,
    () => getPrivacyAbVariantSnapshot(searchParams),
    () => 'disclaimer'
  )
}
